import { apiClient } from './api';
import { ApiResponse } from '@/types';

export type ConversationKind = 'direct' | 'group';
export type ConversationRole = 'owner' | 'admin' | 'member';
export type MessageKind = 'text' | 'system' | 'mediator';

export interface ChatUser {
  id: string;
  name: string;
  username: string;
  avatar?: string | null;
  is_administrator?: boolean;
}

/** Карточка капсулы внутри сообщения; недоступная капсула приходит без полей. */
export interface ChatCapsulePreview {
  id: string;
  isAccessible: boolean;
  title?: string;
  image?: string | null;
  imagePreview?: string | null;
  author?: ChatUser;
  coauthors?: ChatUser[];
  meta?: {
    createdAt: string;
    firstOpenedAt?: string | null;
    scheduledAt?: string | null;
    isOpened: boolean;
  };
  privacy?: 'public' | 'coauthors' | 'selected';
  commentsCount?: number;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  kind: MessageKind;
  text: string;
  sender: ChatUser | null;
  createdAt: string;
  editedAt?: string | null;
  isDeleted: boolean;
  clientId?: string | null;
  systemEvent?: string | null;
  systemPayload?: Record<string, unknown>;
  /** Кого отметило сообщение: так медиатор зовёт человека в разговор. */
  mentions?: ChatUser[];
  capsules: ChatCapsulePreview[];
  /** Присутствует только у локальных сообщений, ещё не подтверждённых сервером. */
  isPending?: boolean;
  isFailed?: boolean;
}

export interface ConversationMember {
  user: ChatUser;
  role: ConversationRole;
  joinedAt: string;
}

export interface Conversation {
  id: string;
  kind: ConversationKind;
  title: string;
  createdAt: string;
  lastMessageAt?: string | null;
  memberCount: number;
  myRole: ConversationRole | null;
  peer: ChatUser | null;
  unreadCount: number;
  lastMessage: ChatMessage | null;
  members?: ConversationMember[];
}

export interface ChatInvite {
  token: string;
  link: string;
  conversationId: string;
  createdBy: ChatUser | null;
  createdAt: string;
  expiresAt?: string | null;
}

export interface ChatInvitePreview {
  conversationId: string;
  title: string;
  memberCount: number;
  invitedBy: ChatUser | null;
  isMember: boolean;
}

/** Состояние медиатора в конкретном групповом чате. */
export interface MediatorState {
  /** Включён ли медиатор на сервере: без скачанной модели его там нет. */
  isAvailable: boolean;
  isEnabled: boolean;
  topic: string;
  lastActionAt?: string | null;
}

/** Карточка, которую медиатор собрал о текущем пользователе (только чтение). */
export interface MediatorMemory {
  summary: string;
  isEnabled: boolean;
  messagesSeen: number;
  updatedAt?: string | null;
}

export interface MessagePage {
  messages: ChatMessage[];
  hasMore: boolean;
}

export interface SendMessagePayload {
  text?: string;
  capsuleIds?: string[];
  clientId?: string;
}

const buildQuery = (params: Record<string, string | number | undefined>) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      query.append(key, String(value));
    }
  });

  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
};

export const chatApi = {
  async getConversations(): Promise<
    ApiResponse<{ conversations: Conversation[] }>
  > {
    return apiClient.get<{ conversations: Conversation[] }>(
      '/chat/conversations/'
    );
  },

  async getConversation(
    conversationId: string
  ): Promise<ApiResponse<{ conversation: Conversation }>> {
    return apiClient.get<{ conversation: Conversation }>(
      `/chat/conversations/${conversationId}/`
    );
  },

  async startDirectConversation(
    userId: string
  ): Promise<ApiResponse<{ conversation: Conversation }>> {
    return apiClient.post<{ conversation: Conversation }>(
      '/chat/conversations/',
      { kind: 'direct', userId }
    );
  },

  async createGroupConversation(
    title: string,
    memberIds: string[]
  ): Promise<ApiResponse<{ conversation: Conversation }>> {
    return apiClient.post<{ conversation: Conversation }>(
      '/chat/conversations/',
      { kind: 'group', title, memberIds }
    );
  },

  async renameConversation(
    conversationId: string,
    title: string
  ): Promise<ApiResponse<{ conversation: Conversation }>> {
    return apiClient.patch<{ conversation: Conversation }>(
      `/chat/conversations/${conversationId}/`,
      { title }
    );
  },

  /**
   * Лента сообщений.
   * `after` — только новые (обновление открытого чата), `before` — страница истории.
   */
  async getMessages(
    conversationId: string,
    params?: { after?: string; before?: string; limit?: number }
  ): Promise<ApiResponse<MessagePage>> {
    const query = buildQuery({
      after: params?.after,
      before: params?.before,
      limit: params?.limit,
    });
    return apiClient.get<MessagePage>(
      `/chat/conversations/${conversationId}/messages/${query}`
    );
  },

  async sendMessage(
    conversationId: string,
    payload: SendMessagePayload
  ): Promise<ApiResponse<{ message: ChatMessage }>> {
    return apiClient.post<{ message: ChatMessage }>(
      `/chat/conversations/${conversationId}/messages/`,
      payload
    );
  },

  async markRead(
    conversationId: string,
    messageId: string
  ): Promise<
    ApiResponse<{ lastReadMessageId: string | null; unreadCount: number }>
  > {
    return apiClient.post(`/chat/conversations/${conversationId}/read/`, {
      messageId,
    });
  },

  async addMembers(
    conversationId: string,
    memberIds: string[]
  ): Promise<ApiResponse<{ conversation: Conversation }>> {
    return apiClient.post<{ conversation: Conversation }>(
      `/chat/conversations/${conversationId}/members/`,
      { memberIds }
    );
  },

  async removeMember(
    conversationId: string,
    userId: string
  ): Promise<ApiResponse<{ conversation?: Conversation }>> {
    return apiClient.delete<{ conversation?: Conversation }>(
      `/chat/conversations/${conversationId}/members/${userId}/`
    );
  },

  async getInviteLink(
    conversationId: string
  ): Promise<ApiResponse<{ invite: ChatInvite }>> {
    return apiClient.post<{ invite: ChatInvite }>(
      `/chat/conversations/${conversationId}/invite/`,
      {}
    );
  },

  async revokeInviteLinks(
    conversationId: string
  ): Promise<ApiResponse<{ revoked: number }>> {
    return apiClient.delete<{ revoked: number }>(
      `/chat/conversations/${conversationId}/invite/`
    );
  },

  async getInvitePreview(
    token: string
  ): Promise<ApiResponse<ChatInvitePreview>> {
    return apiClient.get<ChatInvitePreview>(`/chat/invites/${token}/`);
  },

  async acceptInvite(
    token: string
  ): Promise<ApiResponse<{ conversation: Conversation }>> {
    return apiClient.post<{ conversation: Conversation }>(
      `/chat/invites/${token}/accept/`,
      {}
    );
  },

  /** Капсулы для «/»-подсказки в поле ввода. */
  async getCapsuleSuggestions(
    query: string,
    limit = 8
  ): Promise<ApiResponse<{ capsules: ChatCapsulePreview[] }>> {
    return apiClient.get<{ capsules: ChatCapsulePreview[] }>(
      `/chat/capsule-suggestions/${buildQuery({ query, limit })}`
    );
  },

  async getUnreadCount(): Promise<ApiResponse<{ unreadCount: number }>> {
    return apiClient.get<{ unreadCount: number }>('/chat/unread-count/');
  },

  async getMediatorState(
    conversationId: string
  ): Promise<ApiResponse<MediatorState>> {
    return apiClient.get<MediatorState>(
      `/chat/conversations/${conversationId}/mediator/`
    );
  },

  async setMediatorEnabled(
    conversationId: string,
    isEnabled: boolean
  ): Promise<ApiResponse<MediatorState>> {
    return apiClient.patch<MediatorState>(
      `/chat/conversations/${conversationId}/mediator/`,
      { isEnabled }
    );
  },
};

export const mediatorApi = {
  async getMyMemory(): Promise<ApiResponse<{ memory: MediatorMemory }>> {
    return apiClient.get<{ memory: MediatorMemory }>('/mediator/memory/');
  },
};
